import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatErrorDetail } from "../lib/api";
import PublicHeader from "../components/PublicHeader";
import PublicFooter from "../components/PublicFooter";

const CATEGORIES = ["Waterjet", "Airjet", "Rapier", "Spare Parts", "Technical Services"];

const initial = {
    company_name: "",
    applicant_name: "",
    email: "",
    phone: "",
    category: "",
    gst_number: "",
    address: "",
    city: "",
    state: "Gujarat",
    employee_count: "",
    annual_turnover: "",
    about_company: "",
};

export default function Apply() {
    const [form, setForm] = useState(initial);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const navigate = useNavigate();

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.category) {
            toast.error("Please select a membership category");
            return;
        }
        setSubmitting(true);
        try {
            const payload = { ...form };
            if (payload.employee_count === "") delete payload.employee_count;
            else payload.employee_count = parseInt(payload.employee_count, 10);
            if (!payload.gst_number) delete payload.gst_number;
            if (!payload.annual_turnover) delete payload.annual_turnover;
            if (!payload.about_company) delete payload.about_company;

            const res = await api.post("/applications", payload);
            setResult(res.data);
            toast.success("Application submitted successfully");
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            toast.error(formatErrorDetail(err.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    if (result) {
        return (
            <div className="min-h-screen flex flex-col">
                <PublicHeader />
                <main className="flex-1 max-w-3xl mx-auto px-6 py-24 w-full">
                    <div className="border border-[#E2E8F0] bg-white p-10" data-testid="application-success">
                        <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-3">Application Received</div>
                        <h1 className="font-display text-3xl lg:text-4xl font-bold text-[#0F172A] mb-4">
                            Thank you, {result.applicant_name.split(" ")[0]}.
                        </h1>
                        <p className="text-[#64748B] mb-8 leading-relaxed">
                            Your membership application has been submitted to STMF for review. You&rsquo;ll receive an email update within 7 business days as the committee reviews documents and credentials.
                        </p>
                        <div className="bg-[#F8F9FA] border border-[#E2E8F0] p-6 mb-8">
                            <div className="text-xs uppercase tracking-[0.15em] text-[#64748B] mb-1">Your Application Reference</div>
                            <div className="font-mono text-xl text-[#0F172A] font-semibold" data-testid="application-no">{result.application_no}</div>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={() => navigate("/")}
                                data-testid="back-home-btn"
                                className="px-6 py-3 bg-[#0F172A] text-white text-sm font-medium hover:bg-[#1E293B] transition-colors"
                            >
                                Back to Home
                            </button>
                            <button
                                onClick={() => navigate("/events")}
                                data-testid="view-events-btn"
                                className="px-6 py-3 border border-[#E2E8F0] text-[#0F172A] text-sm font-medium hover:bg-[#F8F9FA] transition-colors"
                            >
                                Browse Events
                            </button>
                        </div>
                    </div>
                </main>
                <PublicFooter />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col">
            <PublicHeader />
            <main className="flex-1 bg-[#F8F9FA]">
                <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16">
                    <div className="mb-12">
                        <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-3">Membership Application</div>
                        <h1 className="font-display text-4xl lg:text-5xl font-bold text-[#0F172A] mb-3 tracking-tight">
                            Join the federation.
                        </h1>
                        <p className="text-[#64748B] max-w-2xl">Complete the form below. Our membership committee reviews all submissions within 7 business days.</p>
                    </div>

                    <form onSubmit={onSubmit} data-testid="application-form" className="bg-white border border-[#E2E8F0] p-8 lg:p-12 space-y-10">
                        <Section title="Company Information" number="01">
                            <Field label="Company Name" required>
                                <input
                                    type="text" required value={form.company_name}
                                    onChange={(e) => setField("company_name", e.target.value)}
                                    data-testid="input-company-name"
                                    className="input" placeholder="ABC Textiles Pvt Ltd"
                                />
                            </Field>
                            <Field label="GST Number">
                                <input type="text" value={form.gst_number}
                                    onChange={(e) => setField("gst_number", e.target.value)}
                                    data-testid="input-gst" className="input" placeholder="24ABCDE1234F1Z5" />
                            </Field>
                            <Field label="Category" required full>
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="category-options">
                                    {CATEGORIES.map((c) => (
                                        <button
                                            key={c} type="button"
                                            onClick={() => setField("category", c)}
                                            data-testid={`category-${c.toLowerCase().replace(/\s+/g, "-")}`}
                                            className={`px-3 py-3 border text-sm font-medium transition-colors ${form.category === c ? "border-[#0F172A] bg-[#0F172A] text-white" : "border-[#E2E8F0] bg-white text-[#64748B] hover:border-[#0F172A] hover:text-[#0F172A]"}`}
                                        >{c}</button>
                                    ))}
                                </div>
                            </Field>
                        </Section>

                        <Section title="Applicant Details" number="02">
                            <Field label="Full Name" required>
                                <input type="text" required value={form.applicant_name}
                                    onChange={(e) => setField("applicant_name", e.target.value)}
                                    data-testid="input-applicant-name" className="input" placeholder="Mr. Amit Shah" />
                            </Field>
                            <Field label="Email" required>
                                <input type="email" required value={form.email}
                                    onChange={(e) => setField("email", e.target.value)}
                                    data-testid="input-email" className="input" placeholder="amit@company.com" />
                            </Field>
                            <Field label="Phone" required>
                                <input type="tel" required value={form.phone}
                                    onChange={(e) => setField("phone", e.target.value)}
                                    data-testid="input-phone" className="input" placeholder="+91 98765 43210" />
                            </Field>
                            <Field label="Employee Count">
                                <input type="number" min="0" value={form.employee_count}
                                    onChange={(e) => setField("employee_count", e.target.value)}
                                    data-testid="input-employees" className="input" placeholder="45" />
                            </Field>
                        </Section>

                        <Section title="Address & Business" number="03">
                            <Field label="Address" required full>
                                <textarea required rows={2} value={form.address}
                                    onChange={(e) => setField("address", e.target.value)}
                                    data-testid="input-address" className="input" placeholder="Plot No. 123, Industrial Estate" />
                            </Field>
                            <Field label="City" required>
                                <input type="text" required value={form.city}
                                    onChange={(e) => setField("city", e.target.value)}
                                    data-testid="input-city" className="input" placeholder="Surat" />
                            </Field>
                            <Field label="State" required>
                                <input type="text" required value={form.state}
                                    onChange={(e) => setField("state", e.target.value)}
                                    data-testid="input-state" className="input" />
                            </Field>
                            <Field label="Annual Turnover">
                                <input type="text" value={form.annual_turnover}
                                    onChange={(e) => setField("annual_turnover", e.target.value)}
                                    data-testid="input-turnover" className="input" placeholder="₹5-10 Crore" />
                            </Field>
                            <Field label="About the Company" full>
                                <textarea rows={4} value={form.about_company}
                                    onChange={(e) => setField("about_company", e.target.value)}
                                    data-testid="input-about" className="input"
                                    placeholder="Brief description of products, services and operations…" />
                            </Field>
                        </Section>

                        <div className="flex flex-col-reverse md:flex-row gap-3 justify-between items-start md:items-center pt-4 border-t border-[#E2E8F0]">
                            <p className="text-xs text-[#64748B]">By submitting, you agree to STMF&rsquo;s membership terms and committee review.</p>
                            <button
                                type="submit" disabled={submitting}
                                data-testid="submit-application-btn"
                                className="px-8 py-4 bg-[#0F172A] text-white text-sm font-semibold uppercase tracking-[0.08em] hover:bg-[#1E293B] transition-colors disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2"
                            >
                                {submitting ? "Submitting…" : "Submit Application"}
                                <span className="text-[#D4AF37]">→</span>
                            </button>
                        </div>
                    </form>
                </div>

                <style>{`
                  .input {
                    width: 100%;
                    padding: 12px 14px;
                    background: #fff;
                    border: 1px solid #E2E8F0;
                    font-size: 14px;
                    color: #0F172A;
                    transition: border-color 0.15s, box-shadow 0.15s;
                  }
                  .input:focus { outline: none; border-color: #0F172A; box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.15); }
                `}</style>
            </main>
            <PublicFooter />
        </div>
    );
}

function Section({ title, number, children }) {
    return (
        <div>
            <div className="flex items-baseline gap-4 mb-6 pb-4 border-b border-[#E2E8F0]">
                <span className="font-mono text-xs text-[#D4AF37]">{number}</span>
                <h2 className="font-display text-xl font-bold text-[#0F172A]">{title}</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">{children}</div>
        </div>
    );
}

function Field({ label, required, children, full }) {
    return (
        <div className={full ? "md:col-span-2" : ""}>
            <label className="block text-xs uppercase tracking-[0.1em] font-medium text-[#64748B] mb-2">
                {label} {required && <span className="text-[#DC2626]">*</span>}
            </label>
            {children}
        </div>
    );
}
